/**
 * FormExportModal
 *
 * PDF export modal: pick a visibility mode, preview the rendered form, and
 * download. Reads the active form/version/type from `useFormStore`.
 *
 * Renders an always-open antd `<Modal>`. Callers gate mount/unmount with
 * `{open && <FormExportModal … />}` so preview rendering and subscriptions
 * only run while the modal is shown — hence the unconditional `open` handed to
 * `usePdfPortal`, which decides for itself when the form has content to capture.
 *
 * Invariant: the rendered blob is cached under *every* input that changes what
 * the PDF contains — the visibility mode and the row scope (`splitQuery`). The
 * workflow send sidebar reopens this modal per target with nothing but the
 * split changed (SYD-2302), so a key that omits the split would hand the next
 * target the previous target's document.
 */

import { FC, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Space, message, theme, Grid } from 'antd';
import type { SelectProps } from 'antd';
import type { SplitQuery, VisibilityTypeId } from '#types/System.types';
import { useFormStore } from '../../../store';
import { usePdfExport } from '../use/usePdfExport';
import { usePdfPortal } from '../use/usePdfPortal';
import { useOrganizationParameters } from '../../../use/useOrganizationParameters';
import {
  PDF_PREVIEW_HEIGHT,
  PDF_MODAL_WIDTH,
  PDF_COLOR_BACKGROUND,
} from '#types/System.Themes.types';
import VisibilityModeSelect from './VisibilityModeSelect';
import PdfPreviewPane from './PdfPreviewPane';
import { Timestamp } from 'firebase/firestore';
import { buildPdfFilename } from '../buildPdfFilename';

const { useBreakpoint } = Grid;

interface FormExportModalProps {
  /** Callback function invoked when the modal is cancelled (e.g., close button, Esc key). */
  onCancel: () => void;
  /** Initial visibility mode to select when the modal opens. Defaults to first PDF export view. */
  initialVisibilityMode?: VisibilityTypeId;
  /** Optional callback triggered after a successful PDF export initiated from the modal. */
  onExport?: () => void;
  /** Optional: when provided, render only this layout structure (e.g., a split section) for preview/export with export theme */
  sectionStructure?: any;
  /**
   * Row scope the preview and the download render at. Forwarded to
   * `usePdfPortal`, which `useArTableSplit` prefers over the on-screen
   * `formStore` split; leave undefined to render the whole document.
   */
  splitQuery?: SplitQuery;
  /** Modal heading. Defaults to `Export PDF`. */
  title?: string;
}

const FormExportModal: FC<FormExportModalProps> = ({
  onCancel,
  initialVisibilityMode,
  onExport,
  sectionStructure,
  splitQuery,
  title = 'Export PDF',
}) => {
  const { token } = theme.useToken();
  const { form, formType, formVersion } = useFormStore();
  const screens = useBreakpoint();
  const { getVisibilityTypesForFormType } = useOrganizationParameters();

  const pdfExportOptions = useMemo(() => {
    const formTypeId = formType?.data?.docId;
    const visibilityTypes = getVisibilityTypesForFormType(formTypeId);
    return visibilityTypes.filter(option => option.isPdfExport === true || option.id === 'pdfview');
  }, [formType?.data?.docId, getVisibilityTypesForFormType]);

  const selectOptions = useMemo<SelectProps['options']>(() => {
    return pdfExportOptions.map(option => ({
      value: option.id,
      label: option.shortLabel ?? option.id,
      title: option.longDescription ?? option.shortLabel ?? option.id
    }));
  }, [pdfExportOptions]);

  const defaultPdfExportMode = useMemo<VisibilityTypeId>(() => {
    return pdfExportOptions[0]?.id ?? 'pdfview';
  }, [pdfExportOptions]);

  const effectiveInitialMode = initialVisibilityMode ?? defaultPdfExportMode;

  const [selectedMode, setSelectedMode] = useState<VisibilityTypeId>(effectiveInitialMode);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const previewPdfUrlRef = useRef<string | null>(null);
  /**
   * Identifies the preview pass whose result may still be shown (SYD-2441). The
   * capture is deferred behind a timeout, so the regeneration effect can start a
   * second pass over the same `contentRef` while the first is in flight; without
   * a token both write their own blob and their own error into the same state.
   */
  const previewPassRef = useRef(0);

  const [cachedPdfBlob, setCachedPdfBlob] = useState<Blob | null>(null);
  const [cachedPdfSettings, setCachedPdfSettings] = useState<{ visibilityMode: VisibilityTypeId; splitKey: string } | null>(null);

  /**
   * Value-compared stand-in for `splitQuery` in the cache key and in the
   * regeneration effect's deps: callers rebuild the query object on every
   * render, so identity would invalidate the cache on every keystroke while
   * omitting it entirely would serve the wrong document.
   */
  const splitKey = useMemo(
    () => (splitQuery
      ? `${splitQuery.splitRef.fieldId}|${splitQuery.splitRef.valuePath}|${splitQuery.mode}|${splitQuery.queryValue}`
      : ''),
    [splitQuery],
  );

  const { portalNode, contentRef: portalContentRef, ready: portalReady } = usePdfPortal({
    open: true,
    sectionStructure,
    visibilityMode: selectedMode,
    splitQuery,
  });

  const revokePreviewBlobUrl = useCallback(() => {
    if (previewPdfUrlRef.current) {
      URL.revokeObjectURL(previewPdfUrlRef.current);
      previewPdfUrlRef.current = null;
    }
  }, []);

  const {
    isLoadingParams,
    paramsError,
    isExporting,
    exportError,
    exportDocument
  } = usePdfExport({ formNumber: form?.data?.formNumber });

  useEffect(() => {
    let isMounted = true;

    setSelectedMode(effectiveInitialMode);
    if (isMounted) {
      setPreviewError(null);
      revokePreviewBlobUrl();
      setPreviewPdfUrl(null);
      setCachedPdfBlob(null);
      setCachedPdfSettings(null);
    }

    if (paramsError) {
      messageApi.error(`Error preparing export parameters: ${paramsError}`);
      if (isMounted) {
        setPreviewError(`Error preparing export parameters: ${paramsError}`);
      }
    }

    return () => {
      isMounted = false;
    };
    // DO NOT REMOVE NEXT LINE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveInitialMode, isLoadingParams, paramsError, messageApi]);

  useEffect(() => {
    if (portalReady && !isLoadingParams && !paramsError) {
      generatePreview(selectedMode, splitKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalReady, isLoadingParams, paramsError, selectedMode, splitKey]);

  const generatePreview = useCallback(async (mode: VisibilityTypeId, modeSplitKey: string) => {
    const pass = previewPassRef.current + 1;
    previewPassRef.current = pass;
    const isCurrentPass = () => previewPassRef.current === pass;

    setPreviewError(null);
    revokePreviewBlobUrl();
    setPreviewPdfUrl(null);
    setIsGeneratingPreview(true);

    setTimeout(async () => {
      if (!isCurrentPass()) return;

      if (!portalContentRef.current) {
        setPreviewError("Content reference is not available for preview.");
        console.error("Content reference is not available for preview.");
        setIsGeneratingPreview(false);
        return;
      }

      try {
        const pdfBlob = await exportDocument({
          contentRef: portalContentRef,
          outputType: 'blob',
        });

        if (!(pdfBlob instanceof Blob)) {
          throw new Error("Preview generation did not return a PDF blob.");
        }

        if (!isCurrentPass()) return;

        setCachedPdfBlob(pdfBlob);
        setCachedPdfSettings({ visibilityMode: mode, splitKey: modeSplitKey });

        const blobUrl = URL.createObjectURL(pdfBlob);
        previewPdfUrlRef.current = blobUrl;
        setPreviewPdfUrl(blobUrl);

      } catch (err) {
        if (!isCurrentPass()) return;
        console.error("Preview generation failed:", err);
        const errorMsg = `Failed to generate preview: ${err instanceof Error ? err.message : String(err)}`;
        setPreviewError(errorMsg);
        messageApi.error(errorMsg);
      } finally {
        if (isCurrentPass()) setIsGeneratingPreview(false);
      }
    }, 100);
  }, [exportDocument, messageApi, portalContentRef, revokePreviewBlobUrl]);

  const handleModeChange = (value: VisibilityTypeId) => {
    setSelectedMode(value);
    setCachedPdfBlob(null);
    setCachedPdfSettings(null);
  };

  const downloadPdfBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    const cleanup = () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      URL.revokeObjectURL(url);
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(cleanup);
    } else {
      setTimeout(cleanup, 0);
    }
  };

  const handleFinalExport = async () => {
    const rawLastModified = formVersion?.data?.meta?.lastModified;
    const lastModified = rawLastModified instanceof Timestamp ? rawLastModified : undefined;
    const filename = buildPdfFilename(
      form?.data?.formNumber,
      formVersion?.data?.formVersionNumber,
      lastModified,
    );

    if (cachedPdfBlob && cachedPdfSettings?.visibilityMode === selectedMode && cachedPdfSettings.splitKey === splitKey) {
      try {
        downloadPdfBlob(cachedPdfBlob, filename);
        messageApi.success("PDF exported successfully.");
        onExport?.();
        onCancel();
        return;
      } catch (err) {
        console.error("Export using cached blob failed:", err);
        messageApi.error(`Failed to export PDF: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
    }

    if (!portalContentRef.current) {
      messageApi.error("Content reference is not available for export.");
      console.error("Content reference is not available for export.");
      return;
    }

    setTimeout(async () => {
      try {
        await exportDocument({
          contentRef: portalContentRef,
          outputType: 'save',
          filename,
        });

        messageApi.success("PDF exported successfully.");
        onExport?.();
        onCancel();
      } catch (err) {
        console.error("Final Export failed:", err);
        messageApi.error(`Failed to export PDF: ${err instanceof Error ? err.message : String(err)}`);
      }
    }, 500);
  };

  const isBusy = isLoadingParams || isExporting || isGeneratingPreview;
  const displayError = exportError || previewError || paramsError;

  return (
    <Modal
      title={title}
      open
      destroyOnHidden
      onOk={handleFinalExport}
      okButtonProps={{
        disabled: isBusy || !!paramsError,
        loading: isExporting
      }}
      onCancel={() => {
        setPreviewError(null);
        onCancel();
      }}
      okText="Download"
      cancelText="Cancel"
      width={PDF_MODAL_WIDTH}
    >
      {contextHolder}
      <Space vertical style={{ width: '100%' }} size={screens.xs ? 8 : 16}>
        <VisibilityModeSelect
          selectedMode={selectedMode}
          options={selectOptions}
          onChange={handleModeChange}
        />
        {!screens.xs && (
          <PdfPreviewPane
            isBusy={isBusy}
            error={displayError}
            previewUrl={previewPdfUrl}
            height={PDF_PREVIEW_HEIGHT}
            backgroundColor={PDF_COLOR_BACKGROUND}
            borderColor={token.colorBorder}
          />
        )}
      </Space>

      {portalNode}
    </Modal>
  );
};

export default FormExportModal;
